import React, { useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useOpcoesDimensao } from '../../hooks/useMetaCompraDimensao';
import type { DimensaoOpcao, DimensaoTipo } from '../../types/metaCompraDimensao';

interface Props {
  dimensao: DimensaoTipo;
  /** Valor selecionado (código + nome). */
  value: DimensaoOpcao | null;
  onChange: (opcao: DimensaoOpcao | null) => void;
  disabled?: boolean;
  hasError?: boolean;
}

/** Debounce simples. */
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

const AutocompleteDimensao: React.FC<Props> = ({ dimensao, value, onChange, disabled, hasError }) => {
  const [query, setQuery] = useState('');
  const [aberto, setAberto] = useState(false);
  const debounced = useDebounced(query, 250);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Habilita query só se aberto e (sem seleção OU usuário digitou pra trocar).
  const { data: opcoes = [], isFetching } = useOpcoesDimensao(dimensao, debounced, aberto && !value);

  // Fecha ao clicar fora
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setAberto(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const trocar = () => {
    onChange(null);
    setQuery('');
    setAberto(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  // Modo "valor selecionado" — mostra chip com botão trocar
  if (value && !aberto) {
    return (
      <div className={`flex items-center gap-1 rounded-iris-sm border bg-accent-soft px-2 py-1.5 ${
        hasError ? 'border-danger' : 'border-accent'
      }`}>
        <span className="font-mono text-[11px] text-accent-text">{value.codigo}</span>
        <span className="flex-1 text-sm text-accent-text">{value.nome}</span>
        {!disabled && (
          <button
            type="button"
            onClick={trocar}
            className="rounded p-0.5 text-accent-text hover:bg-accent/10"
            aria-label="Trocar seleção"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  }

  // Modo "busca" — input com dropdown
  return (
    <div ref={containerRef} className="relative">
      <div className={`flex items-center gap-1 rounded-iris-sm border bg-surface-2 px-2 focus-within:bg-surface focus-within:border-accent focus-within:ring-[3px] focus-within:ring-accent-soft ${
        hasError ? 'border-danger' : 'border-line-strong'
      }`}>
        <MagnifyingGlassIcon className="h-4 w-4 text-tx-3" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setAberto(true); }}
          onFocus={() => setAberto(true)}
          placeholder={`Buscar ${dimensao}...`}
          disabled={disabled}
          className="h-8 flex-1 bg-transparent text-sm text-tx placeholder:text-tx-3 outline-none"
        />
      </div>

      {aberto && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-auto rounded-iris border border-line-strong bg-surface shadow-iris-xl">
          {isFetching ? (
            <p className="p-3 text-center text-xs text-tx-3">Buscando...</p>
          ) : opcoes.length === 0 ? (
            <p className="p-3 text-center text-xs text-tx-3">
              {query.length < 2 ? `Digite pra buscar ${dimensao}` : 'Nenhum resultado.'}
            </p>
          ) : (
            <ul>
              {opcoes.map((o) => (
                <li key={o.codigo}>
                  <button
                    type="button"
                    onClick={() => { onChange(o); setAberto(false); setQuery(''); }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-surface-2"
                  >
                    <span className="font-mono text-tx-3">{o.codigo}</span>
                    <span className="flex-1 text-tx">{o.nome}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default AutocompleteDimensao;
